import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const uid = () => (globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`);
export const withUid = (obj) => ({ ...obj, _uid: obj._uid || uid() });
const stripUid = ({ _uid: _u, ...rest }) => rest;
export const wrapConnection = (str) => ({ _uid: uid(), value: str || '' });
// identifications store connections as [{_uid, value}] for stable React keys, but API expects string[]
const stripIdentificationUids = ({ _uid: _u, connections = [], ...rest }) => ({
  ...rest,
  connections: connections.map(c => (typeof c === 'string' ? c : c.value)).filter(Boolean),
});

export function useEditUnit() {
  const { unitId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({ title: '', description: '', teacher_notes: '' });
  const [standards, setStandards] = useState([withUid({ code: '', description: '' })]);
  const [themes, setThemes] = useState([withUid({ historical_context: '', present_day_connection: '', discussion_points: [''] })]);
  const [vocabulary, setVocabulary] = useState([withUid({ term: '', definition: '', context: '' })]);
  const [identifications, setIdentifications] = useState([
    withUid({ type: 'person', name: '', description: '', significance: '', connections: [wrapConnection('')] }),
  ]);
  const [lessonPlans, setLessonPlans] = useState([]);

  const fetchUnit = useCallback(async () => {
    try {
      setLoading(true);
      const { data: unit } = await axios.get(`${API}/units/${unitId}`);
      setFormData({
        title: unit.title || '',
        description: unit.description || '',
        teacher_notes: unit.teacher_notes || '',
      });
      if (unit.kansas_standards?.length) setStandards(unit.kansas_standards.map(withUid));
      if (unit.themes_to_present?.length) setThemes(unit.themes_to_present.map(withUid));
      if (unit.vocabulary?.length) setVocabulary(unit.vocabulary.map(withUid));
      if (unit.identifications?.length) {
        setIdentifications(unit.identifications.map(item => withUid({
          ...item,
          connections: (item.connections || ['']).map(wrapConnection),
        })));
      }
      if (unit.lesson_plans?.length) setLessonPlans(unit.lesson_plans);
    } catch (error) {
      toast.error('Failed to load unit');
    } finally {
      setLoading(false);
    }
  }, [unitId]);

  useEffect(() => { fetchUnit(); }, [fetchUnit]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      await axios.put(`${API}/units/${unitId}`, {
        ...formData,
        kansas_standards: standards.filter(s => s.code && s.description).map(stripUid),
        themes_to_present: themes.filter(t => t.historical_context && t.present_day_connection).map(stripUid),
        vocabulary: vocabulary.filter(v => v.term && v.definition).map(stripUid),
        identifications: identifications.filter(i => i.name && i.description).map(stripIdentificationUids),
        lesson_plans: lessonPlans,
      });
      toast.success('Unit updated successfully!');
      navigate(`/unit/${unitId}`);
    } catch (error) {
      toast.error('Failed to update unit');
    } finally {
      setSaving(false);
    }
  };

  const listOps = (list, setList) => ({
    add: (blank) => setList([...list, withUid(blank)]),
    remove: (index) => setList(list.filter((_, i) => i !== index)),
    update: (index, field, value) => {
      const updated = [...list];
      updated[index][field] = value;
      setList(updated);
    },
  });

  const standardOps = listOps(standards, setStandards);
  const vocabOps = listOps(vocabulary, setVocabulary);
  const identOps = listOps(identifications, setIdentifications);

  const connectionOps = {
    add: (idIndex) => {
      const updated = [...identifications];
      updated[idIndex].connections.push(wrapConnection(''));
      setIdentifications(updated);
    },
    update: (idIndex, connIndex, value) => {
      const updated = [...identifications];
      updated[idIndex].connections[connIndex] = { ...updated[idIndex].connections[connIndex], value };
      setIdentifications(updated);
    },
    remove: (idIndex, connIndex) => {
      const updated = [...identifications];
      updated[idIndex].connections = updated[idIndex].connections.filter((_, i) => i !== connIndex);
      setIdentifications(updated);
    },
  };

  return {
    unitId, navigate, loading, saving,
    formData, setFormData,
    standards, standardOps,
    vocabulary, vocabOps,
    identifications, identOps, connectionOps,
    handleSubmit,
  };
}
